import React from 'react';
import { Box, Tooltip } from '@mui/material';
import { IngestionBrandInfo } from '../../services/integrationApi';
import { finishedRunStatus } from './connectorHealth';
import { ingest } from './ingestionTokens';
import { RunFacts, isTerminalVerdict } from './runFacts';
import { classifyRunOutcome } from './runOutcome';

const RAIL_BARS = 10;
const RAIL_MAX_HEIGHT = 14;
const RAIL_IDLE_HEIGHT = 3;

interface RunSparklineProps {
  brand: IngestionBrandInfo;
}

/**
 * Rail sparkline: the last ten finished runs, oldest to newest. The brand list
 * carries a verdict per run but no magnitude, so bars encode outcome only — a
 * failed run is full height in the failure color so the newest run reads
 * correctly at a glance, which is the rule the chart exists for.
 */
export const RunSparkline: React.FC<RunSparklineProps> = ({ brand }) => {
  const runs = (brand.recentRuns || [])
    .filter((run) => finishedRunStatus(run.status))
    .slice(0, RAIL_BARS)
    .reverse();

  const pad = RAIL_BARS - runs.length;

  return (
    <Box
      component="span"
      aria-hidden
      sx={{ display: 'flex', alignItems: 'flex-end', gap: '1.5px', height: 16, flexShrink: 0 }}
    >
      {Array.from({ length: pad }, (_, index) => (
        <Box
          key={`idle-${index}`}
          component="span"
          sx={{ width: '3px', height: RAIL_IDLE_HEIGHT, borderRadius: '1px', background: ingest.color.idle }}
        />
      ))}
      {runs.map((run) => {
        const outcome = classifyRunOutcome(run);
        const failed = outcome === 'failed';
        const partial = outcome === 'partial';
        return (
          <Box
            key={run.id}
            component="span"
            sx={{
              width: '3px',
              height: failed || partial ? RAIL_MAX_HEIGHT : Math.round(RAIL_MAX_HEIGHT * 0.6),
              borderRadius: '1px',
              background: failed ? ingest.color.danger : partial ? ingest.color.warning : ingest.color.success,
            }}
          />
        );
      })}
    </Box>
  );
};

const CHART_DAYS = 30;
const CHART_MAX_HEIGHT = 40;
const CHART_IDLE_HEIGHT = 3;
const CHART_SEGMENT_MIN = 8;

export interface RunDay {
  key: string;
  label: string;
  runs: RunFacts[];
  failed: boolean;
  failedCount: number;
  partialCount: number;
  processed: number;
}

/**
 * Buckets the run records into the last 30 calendar days, oldest first.
 *
 * Only terminal runs count. A pending, running or cancelled job has no outcome
 * to report, and letting one into a bucket painted the day green as though a
 * run had succeeded.
 */
export function buildRunDays(runs: RunFacts[], now = new Date()): RunDay[] {
  const days: RunDay[] = [];
  const index = new Map<string, RunDay>();

  for (let offset = CHART_DAYS - 1; offset >= 0; offset -= 1) {
    const date = new Date(now);
    date.setHours(0, 0, 0, 0);
    date.setDate(date.getDate() - offset);
    const day: RunDay = {
      key: date.toISOString().slice(0, 10),
      label: date.toLocaleDateString(undefined, { month: 'short', day: 'numeric' }),
      runs: [],
      failed: false,
      failedCount: 0,
      partialCount: 0,
      processed: 0,
    };
    days.push(day);
    index.set(day.key, day);
  }

  runs.forEach((run) => {
    if (!isTerminalVerdict(run.verdict)) return;
    if (!run.startedAt) return;
    const date = new Date(run.startedAt);
    if (Number.isNaN(date.getTime())) return;
    date.setHours(0, 0, 0, 0);
    const day = index.get(date.toISOString().slice(0, 10));
    if (!day) return;
    day.runs.push(run);
    if (run.verdict === 'Failed') {
      day.failed = true;
      day.failedCount += 1;
    } else if (run.outcome === 'partial') {
      day.partialCount += 1;
    }
    // A day's volume is everything it processed, not its largest single run.
    day.processed += run.processed;
  });

  days.forEach((day) => {
    day.runs.sort((a, b) => {
      const aTime = a.startedAt ? new Date(a.startedAt).getTime() : 0;
      const bTime = b.startedAt ? new Date(b.startedAt).getTime() : 0;
      return aTime - bTime;
    });
  });

  return days;
}

/** Caption for a day column — names the mix, not just "failed" when one of several blew up. */
export function summarizeRunDay(day: RunDay): string {
  if (day.runs.length === 0) return `${day.label} · no runs`;
  const runWord = day.runs.length === 1 ? 'run' : 'runs';
  const parts = [`${day.label} · ${day.runs.length} ${runWord}`];
  if (day.failedCount > 0) parts.push(`${day.failedCount} failed`);
  if (day.partialCount > 0) parts.push(`${day.partialCount} partly failed`);
  parts.push(`${day.processed.toLocaleString()} processed`);
  return parts.join(' · ');
}

interface RunHistoryChartProps {
  runs: RunFacts[];
  onSelectRun?: (runId: string) => void;
}

/**
 * Overview chart: one column per day for the last 30 days. Height still tracks
 * products processed. Color is per run, stacked oldest-at-bottom, so a day
 * with two successes and one failure reads as a mix — not a solid red wash.
 */
export const RunHistoryChart: React.FC<RunHistoryChartProps> = ({ runs, onSelectRun }) => {
  const days = buildRunDays(runs);
  const peak = days.reduce((max, day) => Math.max(max, day.processed), 0);

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'flex-end',
        gap: '4px',
        height: 64,
        padding: '12px 14px',
        border: `1px solid ${ingest.color.borderInner}`,
        borderRadius: '9px',
      }}
    >
      {days.map((day) => {
        const empty = day.runs.length === 0;
        const summary = summarizeRunDay(day);
        const scaled = peak > 0 ? Math.round(CHART_MAX_HEIGHT * (0.25 + 0.75 * (day.processed / peak))) : 12;
        const stackedMin = day.runs.length * CHART_SEGMENT_MIN + Math.max(0, day.runs.length - 1);
        const height = empty
          ? CHART_IDLE_HEIGHT
          : Math.min(CHART_MAX_HEIGHT, Math.max(6, scaled, stackedMin));

        if (empty) {
          return (
            <Tooltip key={day.key} title={summary} arrow disableInteractive>
              <Box
                sx={{
                  flex: 1,
                  minWidth: 0,
                  height,
                  borderRadius: '2px',
                  background: ingest.color.idle,
                }}
              />
            </Tooltip>
          );
        }

        return (
          <Box
            key={day.key}
            role="group"
            aria-label={summary}
            sx={{
              flex: 1,
              minWidth: 0,
              height,
              display: 'flex',
              flexDirection: 'column-reverse',
              gap: '1px',
            }}
          >
            {day.runs.map((run, index) => {
              const failed = run.verdict === 'Failed';
              const partial = run.outcome === 'partial';
              const first = index === 0;
              const last = index === day.runs.length - 1;
              const openable = Boolean(onSelectRun);
              const runLabel = `${day.label} · ${run.kind} · ${partial ? `Completed, ${run.partialFailure}` : run.verdict}`;

              return (
                <Tooltip key={run.id} title={runLabel} arrow disableInteractive>
                  <Box
                    component={openable ? 'button' : 'div'}
                    type={openable ? 'button' : undefined}
                    aria-label={openable ? `${runLabel}. Open run log.` : undefined}
                    onClick={openable ? () => onSelectRun?.(run.id) : undefined}
                    sx={{
                      flex: 1,
                      minHeight: 0,
                      minWidth: 0,
                      padding: 0,
                      border: 0,
                      borderTopLeftRadius: last ? '2px' : 0,
                      borderTopRightRadius: last ? '2px' : 0,
                      borderBottomLeftRadius: first ? '2px' : 0,
                      borderBottomRightRadius: first ? '2px' : 0,
                      background: failed ? ingest.color.danger : partial ? ingest.color.warning : ingest.color.success,
                      cursor: openable ? 'pointer' : 'default',
                      '&:focus-visible': { outline: `2px solid ${ingest.color.accent}`, outlineOffset: 2 },
                    }}
                  />
                </Tooltip>
              );
            })}
          </Box>
        );
      })}
    </Box>
  );
};

export default RunSparkline;
